import type { Settings } from '@/types';
import { useMutation, useQuery } from 'react-query';
import client from './client';
import { API_ENDPOINTS } from './client/api-endpoints';
import { useState } from 'react';
import { FileWithPath } from 'react-dropzone';
import { getPreviewImage } from '@/lib/get-preview-image';
import { useAtom } from 'jotai';
import { couponAtom } from '@/store/checkout';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { setMaintenanceDetails } from './utils/maintenance-utils';

export function useSettings() {
  const { locale } = useRouter();

  const formattedOptions = {
    language: locale,
  };

  const { data, isLoading, error, isFetching } = useQuery<Settings, Error>(
    [API_ENDPOINTS.SETTINGS, formattedOptions],
    ({ queryKey, pageParam }) =>
      client.settings.all(Object.assign({}, queryKey[1], pageParam)),
    {
      staleTime: 12 * 60 * 60 * 1000, // 12 hours
    },
  );
  const { isUnderMaintenance = false, maintenance = {} } = data?.options! ?? {};
  setMaintenanceDetails(isUnderMaintenance, maintenance);
  return {
    settings: data?.options ?? {},
    isLoading,
    error,
    isFetching,
  };
}

export function usePaymentGateways() {
  const { data, isLoading, error } = useQuery<
    Array<{
      id: number;
      name: string;
      slug: string;
      active: boolean;
      options?: { discountPercentage?: number };
    }>,
    Error
  >(['payment-gateways'], () => client.settings.paymentGateways(), {
    staleTime: 12 * 60 * 60 * 1000,
  });

  return {
    paymentGateways: data ?? [],
    isLoading,
    error,
  };
}

export const useUploads = ({ onChange, defaultFiles }: any) => {
  const [files, setFiles] = useState<FileWithPath[]>(
    getPreviewImage(defaultFiles),
  );

  const { mutate: upload, isLoading } = useMutation(client.settings.upload, {
    onSuccess: (data) => {
      if (onChange) {
        const dataAfterRemoveTypename = data?.map(
          ({ __typename, ...rest }: any) => rest,
        );
        onChange(dataAfterRemoveTypename);
        setFiles(getPreviewImage(dataAfterRemoveTypename));
      }
    },
  });

  function handleSubmit(data: File[]) {
    upload(data);
  }

  return { mutate: handleSubmit, isLoading, files };
};

export const useDeleteAttachment = () => {
  const { mutate: deleteAttachment, isLoading } = useMutation(
    client.settings.deleteAttachment,
    {
      onSuccess: (data) => {
        console.log(data);
      },
    },
  );

  return { deleteAttachment, isLoading };
};

export function useSubscription() {
  let [isSubscribed, setIsSubscribed] = useState(false);

  const subscription = useMutation(client.users.subscribe, {
    onSuccess: () => {
      toast.success(
        'Parabéns! Você se inscreveu com sucesso em nossa newsletter.',
      );
      setIsSubscribed(true);
    },
    onError: () => {
      setIsSubscribed(false);
    },
  });

  return {
    ...subscription,
    isSubscribed,
  };
}

export function useVerifyCoupon({
  setCodeFieldError,
  subtotal,
  showSuccessToast = true,
  silent = false,
  keepCurrentOnFailure = false,
  onApplied,
}: {
  setCodeFieldError: (message: string) => void;
  subtotal: number;
  showSuccessToast?: boolean;
  /**
   * Swapping from the coupon picker: a rejected code reports the reason but
   * leaves the currently applied coupon in place.
   */
  keepCurrentOnFailure?: boolean;
  onApplied?: (coupon: any) => void;
  /**
   * When true the hook is used for background re-validation: errors are
   * swallowed silently and the coupon atom is never cleared on failure, so
   * the user can still place the order with the previously-applied coupon.
   * Only a 200 `is_valid: false` response (coupon genuinely became invalid
   * for the current cart) will clear the atom.
   */
  silent?: boolean;
}) {
  const [_, applyCoupon] = useAtom(couponAtom);
  const { mutate, isLoading } = useMutation(client.coupons.verify, {
    onSuccess: (data: any, variables: { code?: string }) => {
      const coupon = data?.coupon;
      // Preserve the code the user typed (unique bulk code). The API may
      // echo the parent shared coupon.code which cannot be re-verified when
      // bulk_codes_only is enabled.
      const redeemedCode = (variables?.code || coupon?.code || '').trim();

      if (
        coupon?.minimum_cart_amount &&
        subtotal < coupon.minimum_cart_amount
      ) {
        if (!keepCurrentOnFailure) applyCoupon(null);
        if (!silent) {
          const remainingAmount = coupon.minimum_cart_amount - subtotal;
          setCodeFieldError(
            `Você precisa adicionar mais R$ ${remainingAmount.toFixed(2)} ao carrinho para usar este cupom.`,
          );
        }
        return;
      }

      if (!data.is_valid) {
        if (!keepCurrentOnFailure) applyCoupon(null);
        if (!silent) {
          // Prefer a human-readable API message; otherwise the pt-BR key
          // that ptBrCommon resolves (do NOT prefix with "common:").
          const apiMessage =
            typeof data?.message === 'string' ? data.message.trim() : '';
          setCodeFieldError(apiMessage || 'error-invalid-coupon');
        }
        return;
      }

      const appliedCoupon = redeemedCode
        ? { ...coupon, code: redeemedCode }
        : coupon;
      applyCoupon(appliedCoupon);
      onApplied?.(appliedCoupon);
      if (showSuccessToast) {
        toast.success('Cupom aplicado com sucesso!');
      }
    },
    onError: (error: any) => {
      // Background re-validation must never wipe the coupon or show UI noise —
      // the user should still be able to place the order and let the server
      // decide whether the code is valid at submission time.
      if (silent) return;

      const data = error?.response?.data;
      const message =
        data?.error?.message ||
        data?.message ||
        'Cupom não encontrado ou inválido';

      setCodeFieldError(message);
    },
  });

  return { mutate, isLoading, applyCoupon };
}
